import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  ReferenceField,
  ChipField,
  FunctionField,
  TopToolbar,
  CreateButton,
  useListContext,
  useNotify,
  useRefresh,
  useGetList,
  Filter,
  SelectInput,
  ReferenceInput,
  DateInput,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  Chip,
  Tooltip,
} from '@mui/material';
import { CheckCircle, Warning, Error, Schedule, Add, Inbox, AutoMode } from '@mui/icons-material';
import { ReminderDialog } from '../../components/dialogs/ReminderDialog';
import { tenantService } from '../../services/tenantService';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageWrapper } from '../../components/PageWrapper';

const ReminderFilters = () => (
  <Filter>
    <SelectInput
      source="status"
      label="Status"
      choices={[
        { id: 'pending', name: 'Pending' },
        { id: 'overdue', name: 'Overdue' },
        { id: 'completed', name: 'Completed' },
      ]}
      multiple
      emptyText="All Statuses"
      alwaysOn
      sx={{
        minWidth: 250,
        '& .MuiOutlinedInput-root': {
          minWidth: 250,
          height: '56px',
        }
      }}
    />
    <SelectInput
      source="autoGenerated"
      label="Reminder Type"
      choices={[
        { id: 'true', name: 'Auto-Generated' },
        { id: 'false', name: 'Manual' },
      ]}
      emptyText="All Types"
      alwaysOn
      sx={{
        minWidth: 250,
        '& .MuiOutlinedInput-root': {
          minWidth: 250,
          height: '56px',
        }
      }}
    />
    <ReferenceInput
      source="userId"
      reference="users"
      label="User"
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
    >
      <SelectInput
        optionText="name"
        emptyText="All Users"
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            height: '56px',
          }
        }}
      />
    </ReferenceInput>
    <ReferenceInput
      source="vehicleId"
      reference="equipment"
      label="Equipment"
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
    >
      <SelectInput
        optionText="name"
        emptyText="All Equipment"
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            height: '56px',
          }
        }}
      />
    </ReferenceInput>
    <ReferenceInput
      source="responsibleUserId"
      reference="users"
      label="Responsible User"
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
    >
      <SelectInput
        optionText="name"
        emptyText="All Responsible Users"
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            height: '56px',
          }
        }}
      />
    </ReferenceInput>
    <DateInput
      source="reminderDateFrom"
      label="Due Date From"
      alwaysOn
      sx={{
        minWidth: 250,
        '& .MuiOutlinedInput-root': {
          minWidth: 250,
          height: '56px',
        }
      }}
    />
    <DateInput
      source="reminderDateTo"
      label="Due Date To"
      alwaysOn
      sx={{
        minWidth: 250,
        '& .MuiOutlinedInput-root': {
          minWidth: 250,
          height: '56px',
        }
      }}
    />
  </Filter>
);

const ReminderListActions = ({ onCreate }: { onCreate: () => void }) => (
  <TopToolbar>
    <Button
      onClick={onCreate}
      startIcon={<Add />}
      variant="contained"
      color="primary"
    >
      Create Reminder
    </Button>
  </TopToolbar>
);

const StatusChip = ({ record }: { record: any }) => {
  const getStatusProps = (status: string, reminderDate?: string) => {
    const now = new Date();
    const due = reminderDate ? new Date(reminderDate) : null;
    const isOverdue = due && due < now && status !== 'completed';

    switch (status?.toLowerCase()) {
      case 'completed':
        return {
          label: 'Completed',
          color: '#4caf50' as const,
          icon: <CheckCircle fontSize="small" />,
        };
      case 'overdue':
        return {
          label: 'Overdue',
          color: '#f44336' as const,
          icon: <Error fontSize="small" />,
        };
      case 'pending':
        if (isOverdue) {
          return {
            label: 'Overdue',
            color: '#f44336' as const,
            icon: <Error fontSize="small" />,
          };
        }
        return {
          label: 'Pending',
          color: '#2196f3' as const,
          icon: <Schedule fontSize="small" />,
        };
      default:
        return {
          label: status || 'Unknown',
          color: '#757575' as const,
          icon: <Warning fontSize="small" />,
        };
    }
  };

  const statusProps = getStatusProps(record.status, record.reminderDate);

  return (
    <Chip
      label={statusProps.label}
      icon={statusProps.icon}
      sx={{
        backgroundColor: `${statusProps.color}20`,
        color: statusProps.color,
        borderColor: `${statusProps.color}40`,
        border: '1px solid',
      }}
      size="small"
    />
  );
};


const ReminderSummaryCards = () => {
  const { data: reminders } = useGetList('reminders', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'reminderDate', order: 'DESC' },
  });

  const getSummaryData = () => {
    if (!reminders) return { total: 0, pending: 0, overdue: 0, completed: 0 };

    const now = new Date();
    const summary = reminders.reduce(
      (acc, reminder) => {
        acc.total++;
        const status = reminder.status?.toLowerCase();
        const reminderDate = reminder.reminderDate ? new Date(reminder.reminderDate) : null;
        const isOverdue = reminderDate && reminderDate < now && status !== 'completed';

        if (status === 'completed') {
          acc.completed++;
        } else if (status === 'overdue' || isOverdue) {
          acc.overdue++;
        } else if (status === 'pending') {
          acc.pending++;
        }

        return acc;
      },
      { total: 0, pending: 0, overdue: 0, completed: 0 }
    );

    return summary;
  };

  const summary = getSummaryData();

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Reminders
            </Typography>
            <Typography variant="h4" component="div">
              {summary.total}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Pending
            </Typography>
            <Typography variant="h4" component="div" sx={{ color: '#2196f3' }}>
              {summary.pending}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Overdue
            </Typography>
            <Typography variant="h4" component="div" sx={{ color: '#f44336' }}>
              {summary.overdue}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Completed
            </Typography>
            <Typography variant="h4" component="div" sx={{ color: '#4caf50' }}>
              {summary.completed}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const EmptyStateComponent = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <CustomEmptyState
        resource="reminders"
        title="No Reminders yet."
        subtitle="Create reminders to track important tasks and maintenance schedules for your vehicles and equipment."
        actionText="Create Reminder"
        onAction={() => setDialogOpen(true)}
        icon={<Inbox sx={{ fontSize: 48, color: 'action.disabled' }} />}
        showBackground={false}
      />
      <ReminderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        recordId={null}
        isEdit={false}
      />
    </>
  );
};

export const RemindersList = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const handleCreate = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleEdit = (id: number) => {
    setEditingId(id);
    setDialogOpen(true);
  };

  return (
    <PageWrapper title="Reminders">
      <Box>
        <ReminderSummaryCards />
        <List
          filters={<ReminderFilters />}
          actions={<ReminderListActions onCreate={handleCreate} />}
          sort={{ field: 'reminderDate', order: 'DESC' }}
          empty={<EmptyStateComponent />}
          filterDefaultValues={{ status: ['pending', 'overdue'] }}
        >
          <Datagrid rowClick={(id) => handleEdit(id as number)} bulkActionButtons={false}>
            <FunctionField
              label="Due Date"
              render={(record: any) => (
                <Box>
                  <Typography variant="body2">
                    {record.reminderDate ? new Date(record.reminderDate).toLocaleDateString() : '-'}
                  </Typography>
                  {record.expirationDate && (
                    <Tooltip title={`Actual expiration: ${new Date(record.expirationDate).toLocaleDateString()}`}>
                      <Typography variant="caption" color="text.secondary">
                        Expires: {new Date(record.expirationDate).toLocaleDateString()}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              )}
            />
            <FunctionField
              label="Type"
              render={(record: any) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: record.reminderType?.color || '#757575',
                      }}
                    />
                    <span>{record.reminderType?.name || 'Unknown'}</span>
                  </Box>
                  {record.autoGenerated && (
                    <Tooltip title="Automatically generated based on expiration dates">
                      <Chip
                        icon={<AutoMode sx={{ fontSize: 14 }} />}
                        label="Auto"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              )}
            />
            <TextField source="notes" label="Description" />
            <FunctionField
              label="User"
              render={(record: any) => (
                <span>{record.user?.name || ''}</span>
              )}
            />
            <FunctionField
              label="Equipment"
              render={(record: any) => (
                <span>{record.equipment?.name || ''}</span>
              )}
            />
            <FunctionField
              label="Responsible User"
              render={(record: any) => (
                <span>{record.responsibleUser?.name || ''}</span>
              )}
            />
            <FunctionField
              label="Status"
              render={(record: any) => <StatusChip record={record} />}
            />
          </Datagrid>
        </List>

        <ReminderDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          recordId={editingId}
          isEdit={!!editingId}
        />
      </Box>
    </PageWrapper>
  );
};